import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';
import { useLocation } from 'react-router-dom';
import SellerNav from '../SellerNav/SellerNav';

import css from './UserNav.module.css';

/**
 * The nav shown under the topbar on account pages.
 *
 * Sellers and businesses get SellerNav — the four grouped areas from
 * P25009-386. Delegating here rather than editing every page means all ten-odd
 * seller pages pick up the grouped nav at once, and none of them can drift back
 * to the old flat row.
 *
 * Private accounts keep the tabs below, which is also how P25009-386 BR-4 is
 * satisfied: Favoriter and Dina bilar are consumer features, so they belong on
 * the private side and are gone from the seller areas entirely.
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, currentUser, sellerBadges } = props;
  const classes = classNames(rootClassName || css.root, className);

  const location = useLocation();
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isSeller = userType === 'seller' || userType === 'business';

  if (isSeller) {
    return (
      <SellerNav
        currentPage={currentPage}
        currentUser={currentUser}
        badges={sellerBadges}
      />
    );
  }

  /* ---- private accounts -------------------------------------------------
   * Consumer tools only. The seller-only tabs that used to live here (Bygg
   * paket, Massuppladdning, Tillval & tjänster, Förvaring, Adresser,
   * Prenumeration) moved into SellerNav's four areas. */
  const tabs = [
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.manageFavouriteListings" />,
      selected: location.pathname === '/favorites',
      disabled: false,
      linkProps: {
        name: 'ManageFavouritesPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.manageSavedQueries" />,
      selected: location.pathname.startsWith('/favorites/saved-queries'),
      disabled: false,
      linkProps: {
        name: 'SavedQueriesPage',
      },
    },
    /* Inköp used to live in the avatar dropdown; P25009-431 empties that to
     * identity rows, so the private profile is where a buyer finds purchases. */
    {
      text: <FormattedMessage id="UserNav.purchases" />,
      selected: currentPage === 'OrderPage',
      disabled: false,
      linkProps: {
        name: 'OrderPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  /* Outer bar paints the full-width white band + hairline; inner
   * caps the content at var(--contentMaxWidth). Without this split
   * the white strip only rendered inside the content cap, leaving
   * grey rails on either side of wide viewports. */
  return (
    <div className={css.bar}>
      <LinkTabNavHorizontal className={classes} tabRootClassName={css.tab} tabs={tabs} />
    </div>
  );
};

UserNav.defaultProps = {
  className: null,
  rootClassName: null,
};

const { string, object } = PropTypes;

UserNav.propTypes = {
  className: string,
  rootClassName: string,
  currentPage: string.isRequired,
  currentUser: object,
  /* Passed through to SellerNav: { drafts, newOrders, payoutBlocked }. Optional —
   * a page with no counts to hand simply shows no badges. */
  sellerBadges: object,
};

export default UserNav;
